import { GridState } from 'ag-grid-community';
import { mapKeys } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

import { AgGridServerTable } from 'core/components';
import { EMPTY, NON_EMPTY } from 'core/constants';
import { TableLayoutType } from 'core/enums';
import { AgGridServerTableProvider } from 'core/states';
import { AgGridGetRowsParams } from 'core/types';
import { removeNullValuesNestedObject } from 'core/utils';
import { useGetDisciplines } from 'corporate/hooks';
import { useGetJobStatuses } from 'jobs/hooks';
import { jobService } from 'jobs/services';
import { IJob } from 'jobs/types';
import { IProject } from 'projects/types';

import { AddJobButton, MyJobsFilter } from './actions';
import { columnDef } from './columns';
import {
  ExportEntityType,
  ExportTimeLogButton,
  UpdateJobNameButton,
  UpdateJobsButton,
} from './selectionActions';

export const JobList = () => {
  const [myJobsFilter, setMyJobsFilter] = useState(true);

  const { records: jobStatuses } = useGetJobStatuses();
  const { records: disciplines } = useGetDisciplines();

  const columnDefs = useMemo(() => columnDef(jobStatuses, disciplines), [jobStatuses, disciplines]);

  const defaultColDef = useMemo(() => ({ autoHeight: true }), []);

  const initialState: GridState = useMemo(() => {
    return {
      sort: {
        sortModel: [{ colId: 'createdAt', sort: 'desc' }],
      },
    };
  }, []);

  const mapFilters = useCallback((filters: Record<string, unknown>) => {
    let mappedFilters = filters;

    mappedFilters = mapKeys(filters, (_, key) => {
      switch (key) {
        case 'ownerUserName':
          return 'ownerUserId';
        case 'requesterUserName':
          return 'requesterUserId';
        default:
          return key;
      }
    });

    const disciplineNameFilter = Array.isArray(mappedFilters?.disciplineName)
      ? mappedFilters.disciplineName
      : [];

    let mappedDisciplineNameFilter = null;
    let mappedDisciplineIdsFilter = null;

    if (disciplineNameFilter.length > 0) {
      const includeEmpty = disciplineNameFilter.includes(EMPTY);
      const includeNonEmpty = disciplineNameFilter.includes(NON_EMPTY);

      mappedDisciplineNameFilter = (() => {
        if (includeEmpty && includeNonEmpty) {
          return null;
        }
        if (includeEmpty) {
          return EMPTY;
        }
        if (includeNonEmpty) {
          return NON_EMPTY;
        }

        return null;
      })();

      mappedDisciplineIdsFilter = disciplineNameFilter.filter(
        (id) => id !== EMPTY && id !== NON_EMPTY
      );
    }

    return {
      ...mappedFilters,
      disciplineName: mappedDisciplineNameFilter,
      disciplineIds: mappedDisciplineIdsFilter,
    };
  }, []);

  const getRows = useCallback(
    async ({ filters, ...rest }: AgGridGetRowsParams) => {
      return await jobService.getJobs(
        removeNullValuesNestedObject({
          filters: {
            ...mapFilters(filters),
            ownedBoms: myJobsFilter ? 1 : 0,
          },
          ...rest,
        })
      );
    },
    [mapFilters, myJobsFilter]
  );

  const actionButtons = useMemo(() => {
    return [<AddJobButton key="addJobButton" />];
  }, []);

  const selectionActionButtons = useMemo(() => {
    return [
      <UpdateJobNameButton key="updateJobNameButton" />,
      <UpdateJobsButton key="updateJobsButton" />,
      <ExportTimeLogButton
        key="exportTimeLogButton"
        entityType={ExportEntityType.BOM}
        isExportLogAllowed={(selectedRows?: IJob[] | IProject[]) =>
          (selectedRows as IJob[])?.every((row) => row.logAccessAllowed)
        }
      />,
    ];
  }, []);

  const rightActionButtons = useMemo(() => {
    return [<MyJobsFilter key="myJobsFilter" value={myJobsFilter} onChange={setMyJobsFilter} />];
  }, [myJobsFilter]);

  return (
    <AgGridServerTableProvider
      tableId="jobListTable"
      entityType="Job"
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      initialState={initialState}
      getRows={getRows}
      layoutType={TableLayoutType.JOB}
      hasLayoutActions
      hasManageFilters
    >
      <AgGridServerTable
        actionButtons={actionButtons}
        rightActionButtons={rightActionButtons}
        selectionActionButtons={selectionActionButtons}
      />
    </AgGridServerTableProvider>
  );
};
